import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ExternalLink, Search, Copy } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'token';

const CATEGORY_ORDER = ['Marketing', 'Index', 'Blog', 'Guides', 'Live Build', 'Status', 'Other'];
const CATEGORY_COLORS = {
  Marketing: 'from-emerald-500 to-teal-500',
  Index: 'from-emerald-500 to-cyan-500',
  Blog: 'from-emerald-500 to-teal-500',
  Guides: 'from-amber-500 to-orange-500',
  'Live Build': 'from-pink-500 to-rose-500',
  Status: 'from-slate-500 to-slate-600',
  Other: 'from-slate-600 to-slate-700',
};

export default function AdminSEO() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/seo/sitemap`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (loc, currentSubmitted) => {
    try {
      await axios.post(
        `${API}/admin/seo/sitemap/mark-submitted`,
        { loc, submitted: !currentSubmitted },
        { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } }
      );
      toast.success(currentSubmitted ? 'Marked pending' : 'Marked submitted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const copy = (s) => {
    navigator.clipboard.writeText(s);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading SEO insights…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <div className="max-w-md p-6 rounded-xl bg-rose-500/10 border border-rose-500/20" data-testid="admin-seo-error">
          <h2 className="text-lg font-bold mb-2">Couldn't load SEO data</h2>
          <p className="text-sm text-slate-400 mb-4">{error}</p>
          <Link to="/dashboard" className="text-sm text-emerald-400 hover:underline">← Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const sortedCategories = CATEGORY_ORDER.filter((c) => data.by_category[c]);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="admin-seo-page">
      <SEOHead
        title="SEO Submission Tracker — CARTIEAI Admin"
        description="Track which sitemap URLs have been submitted to Google Search Console for indexing."
        canonical="https://cartieai.com/admin/seo"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/admin/leads"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors mb-6"
          data-testid="admin-seo-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Admin home</span>
        </Link>

        <div className="flex items-center gap-2 mb-3 text-xs font-bold tracking-widest text-emerald-400 uppercase">
          <Search className="w-3.5 h-3.5" />
          <span>Founder · SEO Console</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3"><BrandTitle>Sitemap submission tracker</BrandTitle></h1>
        <p className="text-slate-400 max-w-2xl mb-8">
          One-click flow to walk every sitemap URL through Google Search Console URL Inspection.
          Submitted URLs typically index in 24-48 hours instead of waiting 2-3 weeks for organic crawl.
        </p>

        {/* Summary stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          <SummaryCard label="Total URLs" value={data.summary.total_urls} testid="admin-seo-stat-total" />
          <SummaryCard label="Submitted" value={data.summary.submitted} accent="text-emerald-400" testid="admin-seo-stat-submitted" />
          <SummaryCard label="Pending" value={data.summary.pending} accent="text-amber-400" testid="admin-seo-stat-pending" />
          <SummaryCard label="Completion" value={`${data.summary.completion_pct}%`} accent="bg-clip-text text-transparent bg-cyan-400 bg-gradient-to-r from-cyan-400 to-emerald-400" testid="admin-seo-stat-pct" />
        </div>

        {/* Progress bar */}
        <div className="mb-10 rounded-xl bg-white/5 border border-white/10 p-4">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="text-slate-400">Indexing progress</span>
            <span className="font-mono text-slate-600">{data.summary.submitted} / {data.summary.total_urls}</span>
          </div>
          <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-emerald-500 bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all duration-700"
              style={{ width: `${data.summary.completion_pct}%` }}
              data-testid="admin-seo-progress-bar"
            />
          </div>
        </div>

        {/* Category sections */}
        {sortedCategories.map((cat) => (
          <section key={cat} className="mb-8" data-testid={`admin-seo-section-${cat.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-lg font-bold">{cat}</h2>
              <span className="text-xs text-slate-400">
                {data.by_category[cat].length} URLs · {data.by_category[cat].filter((u) => u.submitted).length} submitted
              </span>
              <div className={`flex-1 h-px bg-gradient-to-r ${CATEGORY_COLORS[cat] || CATEGORY_COLORS.Other} opacity-30`} />
            </div>
            <div className="space-y-2">
              {data.by_category[cat].map((u) => (
                <UrlRow
                  key={u.loc}
                  url={u}
                  onToggle={() => toggle(u.loc, u.submitted)}
                  onCopy={() => copy(u.loc)}
                />
              ))}
            </div>
          </section>
        ))}

        <div className="mt-10 rounded-xl border border-white/10 bg-white/5 p-5 text-sm text-slate-400">
          <strong className="text-white">Pro tip:</strong> after submitting a batch of URLs, run{' '}
          <code className="text-cyan-400 bg-white/5 px-1.5 py-0.5 rounded text-xs">site:cartieai.com</code>{' '}
          on Google in 48 hours to confirm indexing. Re-submit only if missing.
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent, testid }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3" data-testid={testid}>
      <div className={`text-2xl sm:text-3xl font-bold ${accent || 'text-white'}`}>{value}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
    </div>
  );
}

function UrlRow({ url, onToggle, onCopy }) {
  return (
    <div
      className={`group rounded-lg border px-4 py-3 transition-all flex items-center gap-3 ${
        url.submitted
          ? 'bg-emerald-500/[0.04] border-emerald-500/20'
          : 'bg-white/[0.02] border-white/10 hover:border-white/20'
      }`}
      data-testid={`admin-seo-url-row-${url.loc.replace(/[^a-zA-Z0-9]/g, '-')}`}
    >
      <button
        onClick={onToggle}
        className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
          url.submitted
            ? 'bg-emerald-500 border-emerald-500'
            : 'border-white/20 hover:border-emerald-500/50'
        }`}
        aria-label={url.submitted ? 'Mark pending' : 'Mark submitted'}
        data-testid={`admin-seo-toggle-${url.loc.replace(/[^a-zA-Z0-9]/g, '-')}`}
      >
        {url.submitted && <CheckCircle2 className="w-3.5 h-3.5 text-slate-900" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-mono text-slate-700 truncate">{url.loc.replace('https://cartieai.com', '')}</div>
        <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-3">
          <span>Priority: <span className="text-slate-400">{url.priority}</span></span>
          {url.lastmod && <span>· Updated: <span className="text-slate-400">{url.lastmod}</span></span>}
        </div>
      </div>

      <button
        onClick={onCopy}
        className="text-xs px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-slate-600 transition-colors flex items-center gap-1.5 opacity-0 group-hover:opacity-100"
        data-testid={`admin-seo-copy-${url.loc.replace(/[^a-zA-Z0-9]/g, '-')}`}
      >
        <Copy className="w-3 h-3" />
        Copy
      </button>

      <a
        href={url.gsc_inspect_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs px-3 py-1.5 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 transition-colors flex items-center gap-1.5 font-semibold"
        data-testid={`admin-seo-inspect-${url.loc.replace(/[^a-zA-Z0-9]/g, '-')}`}
      >
        <span>Inspect in GSC</span>
        <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}
